// Indoor-time report exporters — a styled Excel workbook and a professional PDF,
// both with company header + footer. Shared column set so the two match.
//
// A "row" is: { date, login, name, area, startIndoor, finishIndoor, parcels, pph, comment }
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

const GOLD = rgb(0.788, 0.659, 0.298);
const INK  = rgb(0.10, 0.10, 0.18);

// Display columns, in order. width = Excel char width; pw = PDF point width (a
// relative weight, scaled to fit the page). pdfLabel keeps the PDF header short so
// it doesn't overrun a narrow portrait column.
const COLS = [
  { key: 'date',         label: 'Date',            pdfLabel: 'Date',     width: 12, pw: 58,  align: 'left'  },
  { key: 'login',        label: 'Login',           pdfLabel: 'Login',    width: 12, pw: 62,  align: 'left'  },
  { key: 'name',         label: 'Driver',          pdfLabel: 'Driver',   width: 22, pw: 132, align: 'left'  },
  { key: 'area',         label: 'Area',            pdfLabel: 'Area',     width: 8,  pw: 34,  align: 'left'  },
  { key: 'startIndoor',  label: 'Start (indoor)',  pdfLabel: 'Start',    width: 12, pw: 40,  align: 'right' },
  { key: 'finishIndoor', label: 'Finish (onboard)',pdfLabel: 'Onboard',  width: 14, pw: 48,  align: 'right' },
  { key: 'indoorHrs',    label: 'Indoor hrs',      pdfLabel: 'Ind hrs',  width: 11, pw: 44,  align: 'right' },
  { key: 'parcels',      label: 'Parcels',         pdfLabel: 'Parcels',  width: 10, pw: 44,  align: 'right' },
  { key: 'pph',          label: 'Parcels/hr',      pdfLabel: 'P/hr',     width: 11, pw: 40,  align: 'right' },
  { key: 'comment',      label: 'Comments',        pdfLabel: 'Comments', width: 46, pw: 120, align: 'left'  },
];

const cell = (r, key) => {
  const v = r[key];
  if (v == null || v === '') return '';
  return String(v);
};

function fileStamp() {
  // Callers pass a preformatted range; the filename just needs a safe token.
  return new Date().toISOString().slice(0, 10);
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

// ── Excel ─────────────────────────────────────────────────────────────────────
export async function downloadIndoorXlsx({ rows, rangeLabel, generatedAt }) {
  const mod = await import('exceljs/dist/exceljs.min.js');
  const ExcelJS = (mod.default && mod.default.Workbook) ? mod.default : (mod.Workbook ? mod : (mod.default || mod));
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Hollypaul App Engine';
  const ws = wb.addWorksheet('Indoor Time', {
    views: [{ state: 'frozen', ySplit: 4 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0, margins: { left: 0.4, right: 0.4, top: 0.6, bottom: 0.6, header: 0.3, footer: 0.3 } },
    headerFooter: {
      oddHeader: '&L&"Helvetica,Bold"&14PH BOUVENG / HOLLYPAUL&R&"Helvetica"&10Indoor Time Report',
      oddFooter: `&L&8${(generatedAt || '').replace(/&/g, '&&')}&C&8Confidential&R&8Page &P of &N`,
    },
  });

  const nCols = COLS.length;
  COLS.forEach((c, i) => { ws.getColumn(i + 1).width = c.width; });

  // Title block (rows 1–3)
  ws.mergeCells(1, 1, 1, nCols);
  const t1 = ws.getCell(1, 1);
  t1.value = 'Indoor Time Report';
  t1.font = { name: 'Helvetica', size: 16, bold: true, color: { argb: 'FF1A1A2E' } };
  ws.getRow(1).height = 22;

  ws.mergeCells(2, 1, 2, nCols);
  const t2 = ws.getCell(2, 1);
  t2.value = `${rangeLabel}    ·    Start of shift → onboard image capture`;
  t2.font = { name: 'Helvetica', size: 10, color: { argb: 'FF6B6B76' } };

  ws.mergeCells(3, 1, 3, nCols);
  const t3 = ws.getCell(3, 1);
  t3.value = generatedAt || '';
  t3.font = { name: 'Helvetica', size: 9, italic: true, color: { argb: 'FF9A9AA2' } };

  // Header row (row 4)
  const headerRow = ws.getRow(4);
  COLS.forEach((c, i) => {
    const h = headerRow.getCell(i + 1);
    h.value = c.label;
    h.font = { name: 'Helvetica', size: 10, bold: true, color: { argb: 'FF1A1A2E' } };
    h.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC9A84C' } };
    h.alignment = { vertical: 'middle', horizontal: c.align, wrapText: true };
    h.border = { bottom: { style: 'thin', color: { argb: 'FF888888' } } };
  });
  headerRow.height = 20;

  // Data rows
  rows.forEach((r, ri) => {
    const row = ws.getRow(5 + ri);
    COLS.forEach((c, i) => {
      const target = row.getCell(i + 1);
      if (c.key === 'parcels' || c.key === 'pph' || c.key === 'indoorHrs') {
        const num = r[c.key];
        target.value = (num == null || num === '') ? '' : Number(num);
        if (c.key === 'pph' && num != null) target.numFmt = '0.0';
        if (c.key === 'indoorHrs' && num != null) target.numFmt = '0.00';
      } else {
        target.value = cell(r, c.key);
      }
      target.font = { name: 'Helvetica', size: 10, color: { argb: 'FF222228' } };
      target.alignment = { vertical: 'top', horizontal: c.align, wrapText: c.key === 'comment' };
      target.border = { bottom: { style: 'hair', color: { argb: 'FFDDDDDD' } } };
    });
    if (ri % 2 === 1) {
      row.eachCell({ includeEmpty: true }, (cc) => {
        cc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF7F5EF' } };
      });
    }
  });

  const buf = await wb.xlsx.writeBuffer();
  triggerDownload(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Indoor-Time-Report_${fileStamp()}.xlsx`);
}

// ── PDF ───────────────────────────────────────────────────────────────────────
export async function downloadIndoorPdf({ rows, rangeLabel, generatedAt }) {
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);

  const PW = 595.28, PH = 841.89;       // A4 portrait
  const M = 30, BAND = 54, FOOT = 34;
  const tableW = PW - 2 * M;

  // Scale PDF column widths to the available table width.
  const rawW = COLS.reduce((a, c) => a + c.pw, 0);
  const scale = tableW / rawW;
  const colW = COLS.map((c) => c.pw * scale);

  let page, y, pageNum = 0;

  // Trim a single-line string with an ellipsis so it never spills into the next
  // column (long driver names were overlapping Area).
  const clip = (text, f, size, maxW) => {
    let s = String(text == null ? '' : text);
    if (f.widthOfTextAtSize(s, size) <= maxW) return s;
    while (s.length && f.widthOfTextAtSize(s + '…', size) > maxW) s = s.slice(0, -1);
    return s + '…';
  };

  const wrap = (text, f, size, maxW) => {
    const words = String(text || '').split(/\s+/).filter(Boolean);
    const lines = []; let line = '';
    for (const w of words) {
      const trial = line ? line + ' ' + w : w;
      if (f.widthOfTextAtSize(trial, size) > maxW && line) { lines.push(line); line = w; }
      else line = trial;
    }
    if (line) lines.push(line);
    return lines.length ? lines : [''];
  };

  const drawHeaderBand = () => {
    page.drawRectangle({ x: 0, y: PH - BAND, width: PW, height: BAND, color: INK });
    page.drawRectangle({ x: 0, y: PH - BAND - 3, width: PW, height: 3, color: GOLD });
    page.drawText('HOLLYPAUL', { x: M, y: PH - 30, size: 18, font: bold, color: GOLD });
    page.drawText('PH BOUVENG PTY LTD', { x: M, y: PH - 44, size: 8, font, color: rgb(0.8, 0.8, 0.86) });
    const title = 'Indoor Time Report';
    page.drawText(title, { x: PW - M - bold.widthOfTextAtSize(title, 14), y: PH - 28, size: 14, font: bold, color: rgb(1, 1, 1) });
    const sub = rangeLabel || '';
    page.drawText(sub, { x: PW - M - font.widthOfTextAtSize(sub, 9), y: PH - 43, size: 9, font, color: rgb(0.72, 0.72, 0.8) });
  };

  const drawColHeaders = () => {
    y = PH - BAND - 22;
    page.drawRectangle({ x: M, y: y - 4, width: tableW, height: 18, color: rgb(0.95, 0.93, 0.86) });
    let x = M;
    COLS.forEach((c, i) => {
      const lbl = clip(c.pdfLabel || c.label, bold, 8, colW[i] - 6);
      const tx = c.align === 'right' ? x + colW[i] - 4 - bold.widthOfTextAtSize(lbl, 8) : x + 4;
      page.drawText(lbl, { x: tx, y: y + 1, size: 8, font: bold, color: INK });
      x += colW[i];
    });
    y -= 10;
  };

  const newPage = () => {
    page = pdf.addPage([PW, PH]); pageNum += 1;
    drawHeaderBand(); drawColHeaders();
  };
  newPage();

  const size = 7.5, lineH = 10.5, topPad = 9, botPad = 5;
  const commentIdx = COLS.findIndex((c) => c.key === 'comment');
  const nameIdx = COLS.findIndex((c) => c.key === 'name');
  // Baseline of line `li` within the row whose top is the current `y`. topPad
  // keeps the first line clear of the divider above it; botPad clears the one below.
  const baseY = (li) => y - topPad - li * lineH;
  rows.forEach((r) => {
    // Comments and driver names may wrap → size the row to the tallest of them.
    const commentLines = wrap(cell(r, 'comment'), font, size, colW[commentIdx] - 8);
    const nameLines = wrap(cell(r, 'name'), bold, size, colW[nameIdx] - 6);
    const maxLines = Math.max(1, commentLines.length, nameLines.length);
    const rowH = (maxLines - 1) * lineH + topPad + botPad;
    if (y - rowH < FOOT) newPage();

    let x = M;
    COLS.forEach((c, i) => {
      if (c.key === 'comment') {
        commentLines.forEach((ln, li) => page.drawText(ln, { x: x + 4, y: baseY(li), size, font, color: rgb(0.15, 0.15, 0.2) }));
      } else if (c.key === 'name') {
        nameLines.forEach((ln, li) => page.drawText(ln, { x: x + 4, y: baseY(li), size, font: bold, color: rgb(0.13, 0.13, 0.18) }));
      } else {
        let val = cell(r, c.key);
        if (c.key === 'pph' && r.pph != null && r.pph !== '') val = Number(r.pph).toFixed(1);
        if (c.key === 'indoorHrs' && r.indoorHrs != null && r.indoorHrs !== '') val = Number(r.indoorHrs).toFixed(2);
        const isNum = ['parcels', 'pph', 'indoorHrs'].includes(c.key);
        val = clip(val, font, size, colW[i] - 6);
        const tw = font.widthOfTextAtSize(val, size);
        const tx = (c.align === 'right' || isNum) ? x + colW[i] - 4 - tw : x + 4;
        page.drawText(val, { x: tx, y: baseY(0), size, font, color: rgb(0.13, 0.13, 0.18) });
      }
      x += colW[i];
    });
    y -= rowH;
    // Hairline divider sits on the row boundary, in the gap — clear of both rows' text.
    page.drawLine({ start: { x: M, y }, end: { x: PW - M, y }, thickness: 0.4, color: rgb(0.9, 0.9, 0.92) });
  });

  // Footers with final page count
  const pages = pdf.getPages();
  pages.forEach((pg, i) => {
    pg.drawLine({ start: { x: M, y: FOOT - 6 }, end: { x: PW - M, y: FOOT - 6 }, thickness: 0.5, color: rgb(0.8, 0.8, 0.8) });
    pg.drawText(`PH BOUVENG PTY LTD · Indoor Time Report · ${generatedAt || ''}`, { x: M, y: FOOT - 18, size: 7, font, color: rgb(0.5, 0.5, 0.5) });
    const pn = `Page ${i + 1} of ${pages.length}`;
    pg.drawText(pn, { x: PW - M - font.widthOfTextAtSize(pn, 7), y: FOOT - 18, size: 7, font, color: rgb(0.5, 0.5, 0.5) });
    pg.drawText('Confidential', { x: PW / 2 - font.widthOfTextAtSize('Confidential', 7) / 2, y: FOOT - 18, size: 7, font, color: rgb(0.5, 0.5, 0.5) });
  });

  const bytes = await pdf.save();
  triggerDownload(new Blob([bytes], { type: 'application/pdf' }), `Indoor-Time-Report_${fileStamp()}.pdf`);
}
